/**
 * How the back office writes dates, times and money.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS A SETTING AND WHAT IS NOT
 * ---------------------------------------------------------------------------
 *
 * A date on the wire and a date in storage are ISO 8601, always, and every date
 * the engine handles is a DAY NUMBER — days since 1970-01-01 UTC — which has no
 * timezone to be wrong about. None of that is configurable, and none of it should
 * be: two systems agreeing on `2027-07-03` is what makes an import, an export and
 * a channel feed possible at all.
 *
 * What a park may choose is how those values are DISPLAYED, and nothing else:
 *
 *   - `dateFormat` — the order of the parts. `dmy` is what a Dutch park expects,
 *     `mdy` what an American one does, `iso` what a technician does.
 *   - `timeFormat` — 24-hour or 12-hour with AM/PM.
 *   - `timeZone` — whose clock a TIMESTAMP is read in. The park's, not the
 *     viewer's: an operator in another country looking at when an enquiry
 *     arrived wants the park's evening, not their own afternoon.
 *
 * This module is the only place any of it happens. `test/display-format.spec.ts`
 * asserts that no component formats a date or an amount itself, because a single
 * `toLocaleDateString('en-GB')` in a component is how a setting becomes a lie.
 *
 * ---------------------------------------------------------------------------
 * WHY THE CONFIGURATION IS MODULE-LEVEL AND NOT A CONTEXT
 * ---------------------------------------------------------------------------
 *
 * `makeFormats` is pure and takes the settings, which is what the tests use. The
 * module keeps one configured instance on top of that, set once when the park's
 * settings load, because the alternative is a hook in each of forty-odd call
 * sites to deliver a value that is the same for all of them for the lifetime of
 * the session. The state is write-once-per-load and never read during render in
 * a way that could differ between two components in one pass.
 */

/** Day = days since 1970-01-01 UTC, the engine's unit. */
export type Day = number;

const MS = 86_400_000;

export function toDay(iso: string): Day | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  return Math.round(Date.UTC(+m[1], +m[2] - 1, +m[3]) / MS);
}

export function toIso(d: Day): string {
  return new Date(d * MS).toISOString().slice(0, 10);
}

/**
 * The order the parts of a date appear in. Display only.
 *
 * `locale` is the default and means "whatever the interface language writes",
 * which is the right answer for almost everybody: a Dutch operator reading a
 * Dutch dashboard wants `3 jul` without having configured anything. The explicit
 * orders exist for the park whose language and conventions disagree — and
 * because es10 offered the choice, so somebody uses it.
 *
 * Defaulting to a fixed order instead would pick one park's convention and call
 * it universal.
 */
export type DateFormat = 'locale' | 'dmy' | 'mdy' | 'iso';

/** Whether a clock time carries AM/PM. Display only. */
export type TimeFormat = '24h' | '12h';

export interface DisplaySettings {
  dateFormat?: DateFormat;
  /**
   * The interface language, which decides the date order unless `dateFormat`
   * overrides it. Absent means English.
   */
  locale?: string;
  timeFormat?: TimeFormat;
  /** IANA zone, e.g. `Europe/Amsterdam`. */
  timeZone?: string;
  /** ISO code the park's amounts are in. A label; there is no conversion. */
  currency?: string;
}

/**
 * The locale each date order is spelled with.
 *
 * A locale rather than a hand-built string because month names, separators and
 * the position of the year are a language's business, not ours — `nl-NL` writes
 * `3 jul` and `en-US` writes `Jul 3`, and neither is something to reimplement.
 *
 * `en-CA` is used for `iso` because it is the one common locale whose numeric
 * format is genuinely `YYYY-MM-DD`.
 */
const LOCALE: Record<Exclude<DateFormat, 'locale'>, string> = {
  dmy: 'nl-NL',
  mdy: 'en-US',
  iso: 'en-CA',
};

/** English rather than the browser's: a shared screenshot must read the same. */
const DEFAULT_LOCALE = 'en-GB';

/**
 * A bare language tag is not enough.
 *
 * `Intl` resolves plain `'en'` to American conventions — `Jun 01` and
 * `€1,234.50` — which is not what an English-language dashboard for a European
 * park should say. So the region is named. Only the languages with a catalogue
 * are listed; anything else passes through and takes whatever `Intl` decides,
 * which is better than refusing to render.
 */
const REGION: Record<string, string> = { en: 'en-GB', nl: 'nl-NL' };

function languageLocale(s: DisplaySettings): string {
  const ui = (s.locale ?? '').slice(0, 2).toLowerCase();
  if (!ui) return DEFAULT_LOCALE;
  return REGION[ui] ?? ui;
}

/** The locale DATES are spelled with: the park's override, else the language. */
function dateLocale(s: DisplaySettings): string {
  const chosen = s.dateFormat ?? 'locale';
  return chosen === 'locale' ? languageLocale(s) : LOCALE[chosen];
}

/**
 * A zone `Intl` will accept, else UTC.
 *
 * Same argument as the currency: an unrecognised zone throws, and this runs during
 * render on a value that came from storage. A park whose zone was mistyped by an
 * import should see UTC timestamps, not a blank screen.
 */
function usableZone(zone: string | undefined): string {
  if (!zone) return 'UTC';
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return zone;
  } catch {
    return 'UTC';
  }
}

export interface Formats {
  /** "3 jul" — a column head or a table cell. */
  shortDate: (d: Day) => string;
  /** "vr" — one weekday, abbreviated. */
  weekday: (d: Day) => string;
  /** "vrijdag 3 juli 2027" — a sentence. */
  longDate: (d: Day) => string;
  /** A timestamp, in the PARK's zone. Takes an ISO instant, not a day. */
  dateTime: (iso: string) => string;
  /**
   * Which DAY an instant fell on, in the park's zone.
   *
   * The right answer to "when did this enquiry arrive", and not the same thing as
   * the first ten characters of the ISO string. An enquiry submitted at 00:30 in
   * Amsterdam is stored as 22:30 the previous day in UTC, so truncating the text
   * reports it as having arrived yesterday — which is how a park loses track of
   * which morning's post is which.
   */
  stampDate: (iso: string) => string;
  money: (n: number) => string;
  /** Rounded to the nearest unit, for a dense grid. */
  money0: (n: number) => string;
  /** A plain grouped number — a count of cells, a number of units. */
  count: (n: number) => string;
  /** "July 2027" — a month heading. */
  month: (d: Day) => string;
  /** "3" — the day of the month, for a calendar cell. */
  dayOfMonth: (d: Day) => string;
}

/**
 * Formatters for one set of park settings.
 *
 * Pure, and the thing tests should use. Built once per settings object rather
 * than per call: an `Intl.DateTimeFormat` costs real milliseconds to construct
 * and a season grid asks for hundreds.
 */
export function makeFormats(s: DisplaySettings = {}): Formats {
  const locale = dateLocale(s);
  // Money follows the LANGUAGE, not the date-order override.
  //
  // They are different questions. A Dutch park that picks `iso` dates so its
  // spreadsheets sort has said nothing about wanting `€1,234.50` — a decimal
  // point where they expect a comma is a misread invoice, and the two conventions
  // differ by a factor of a thousand.
  const numberLocale = languageLocale(s);
  // `Intl` THROWS on a code it does not recognise, and this function is called
  // during render from settings that come out of a database and out of a field
  // somebody is still typing in. A half-typed `EU` took the whole screen down.
  //
  // So a code that is not three letters falls back to EUR rather than throwing. It
  // is a label; being wrong about it is a cosmetic fault, and refusing to render
  // is not. The API validates the value on the way in, which is where a refusal
  // belongs.
  const asked = (s.currency ?? '').trim().toUpperCase();
  const currency = /^[A-Z]{3}$/.test(asked) ? asked : 'EUR';
  // Absent means UTC rather than the viewer's own zone. A fallback that varies
  // per operator means two people reading one enquiry disagree about the day it
  // arrived, and neither has any way to notice.
  const timeZone = usableZone(s.timeZone);

  // A DATE is pinned to UTC whatever the park's zone is. Day 20_638 is
  // 2026-07-01 and nothing else; rendering it in Europe/Amsterdam would shift it
  // to the previous evening and print the wrong day.
  // Two-digit day, so a column of dates in a season grid stays aligned. The one
  // place a leading zero earns its keep.
  //
  // `iso` gets a NUMERIC month, because a short month name defeats the reason
  // somebody picks it: `en-CA` with `month: 'short'` writes `Jun 01`, which is
  // neither ISO nor sortable. A park choosing this order wants `06-01`.
  const short = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: s.dateFormat === 'iso' ? '2-digit' : 'short',
    timeZone: 'UTC',
  });
  const wd = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const long = new Intl.DateTimeFormat(locale, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
  // A TIMESTAMP is an instant, so this one does take the park's zone.
  const stamp = new Intl.DateTimeFormat(locale, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    hour12: (s.timeFormat ?? '24h') === '12h',
    timeZone,
  });
  const stampDay = new Intl.DateTimeFormat(locale, {
    year: 'numeric', month: 'short', day: '2-digit', timeZone,
  });
  const amount = new Intl.NumberFormat(numberLocale, {
    style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const rounded = new Intl.NumberFormat(numberLocale, {
    style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
  // Grouping follows the language for the same reason money does: `12.500` reads
  // as twelve and a half in English and as twelve thousand five hundred in Dutch.
  const plain = new Intl.NumberFormat(numberLocale);
  // UTC, like every other DATE formatter here: a day number has no zone, and
  // rendering one in the park's evening would print the month before on the 1st.
  const monthName = new Intl.DateTimeFormat(locale, {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
  const dayNum = new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' });

  return {
    shortDate: (d) => short.format(new Date(d * MS)),
    weekday: (d) => wd.format(new Date(d * MS)),
    longDate: (d) => long.format(new Date(d * MS)),
    dateTime: (iso) => {
      const t = new Date(iso);
      // An unparseable timestamp is shown as it arrived. "Invalid Date" in a
      // table tells an operator nothing; the raw value at least identifies the
      // row that is wrong.
      return Number.isNaN(t.getTime()) ? iso : stamp.format(t);
    },
    stampDate: (iso) => {
      const t = new Date(iso);
      return Number.isNaN(t.getTime()) ? iso : stampDay.format(t);
    },
    money: (n) => amount.format(n),
    money0: (n) => rounded.format(Math.round(n)),
    count: (n) => plain.format(n),
    month: (d) => monthName.format(new Date(d * MS)),
    dayOfMonth: (d) => dayNum.format(new Date(d * MS)),
  };
}

/**
 * The configured instance. Defaults are the park this was written for, so a
 * dashboard that has not loaded settings yet renders sensibly rather than
 * differently.
 */
let current: Formats = makeFormats();

/** Called once when the park's settings arrive. */
export function applyDisplaySettings(s: DisplaySettings): void {
  current = makeFormats(s);
}

/** For tests, so one spec's settings cannot leak into the next. */
export function resetDisplaySettings(): void {
  current = makeFormats();
}

export const shortDate = (d: Day): string => current.shortDate(d);
export const weekday = (d: Day): string => current.weekday(d);
export const longDate = (d: Day): string => current.longDate(d);
export const dateTime = (iso: string): string => current.dateTime(iso);
export const stampDate = (iso: string): string => current.stampDate(iso);
export const money = (n: number): string => current.money(n);
export const money0 = (n: number): string => current.money0(n);
export const count = (n: number): string => current.count(n);
export const monthLabel = (d: Day): string => current.month(d);
export const dayOfMonth = (d: Day): string => current.dayOfMonth(d);

/** Validated categorical slots, assigned in fixed order and never cycled by rank. */
export const SERIES = ['--s1', '--s2', '--s3', '--s4', '--s5', '--s6', '--s7', '--s8'] as const;

/**
 * Slots whose light-mode fill sits under 3:1 on the surface. Those blocks take
 * dark ink so the label on top of the fill stays legible; the fill itself is
 * allowed to be low-contrast because identity is carried by the label, not the
 * colour (the palette's relief rule).
 */
export const DARK_INK = new Set(['--s3', '--s4', '--s5']);

export function seriesVar(index: number): string {
  return SERIES[index % SERIES.length];
}
